import * as React from 'react';
import { Column } from '@tanstack/react-table';
import { Check, Filter } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import i18n from '@/utils/i18n';

export interface DataTableFacetedFilterProps<TData, TValue> {
  column?: Column<TData, TValue>;
  title?: string;
  options: {
    key: string;
    label: string;
    value: string;
    icon?: React.ComponentType<{ className?: string }>;
  }[];
}

export function DataTableFacetedFilter<TData, TValue>({
  column,
  title,
  options,
}: DataTableFacetedFilterProps<TData, TValue>) {
  const facets = column?.getFacetedUniqueValues();
  const selectedValues = new Set(column?.getFilterValue() as string[]);

  if (!column) {
    return null;
  }

  const atLeastOneHasValue = options.some((option) =>
    facets?.get(option.value),
  );

  const showSearch = options.length > 4;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="tw:h-8 tw:border-dashed">
          <Filter />
          {title}
          {selectedValues?.size > 0 && (
            <>
              <Separator orientation="vertical" className="tw:mx-2 tw:h-4" />
              <Badge
                variant="secondary"
                className="tw:rounded-sm tw:px-1 tw:font-normal tw:lg:hidden"
              >
                {selectedValues.size}
              </Badge>
              <div className="tw:hidden tw:gap-1 tw:lg:flex">
                {selectedValues.size > 2 ? (
                  <Badge
                    variant="secondary"
                    className="tw:rounded-sm tw:px-1 tw:font-normal"
                  >
                    {selectedValues.size} selected
                  </Badge>
                ) : (
                  options
                    .filter((option) => selectedValues.has(option.value))
                    .map((option) => (
                      <Badge
                        variant="secondary"
                        key={option.key}
                        className="tw:rounded-sm tw:px-1 tw:font-normal"
                      >
                        {option.label}
                      </Badge>
                    ))
                )}
              </div>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="tw:w-fit tw:min-w-[120px] tw:max-w-[350px] tw:p-0"
        align="start"
      >
        <Command>
          {showSearch && <CommandInput placeholder={title} />}
          <CommandList>
            <CommandEmpty>{i18n.t('ItemLocation:noResults')}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const isSelected = selectedValues.has(option.value);
                return (
                  <CommandItem
                    key={option.key}
                    className="tw:[&>svg:last-child]:hidden"
                    onSelect={() => {
                      if (isSelected) {
                        selectedValues.delete(option.value);
                      } else {
                        selectedValues.add(option.value);
                      }
                      const filterValues = Array.from(selectedValues);
                      column?.setFilterValue(
                        filterValues.length ? filterValues : undefined,
                      );
                    }}
                  >
                    <div
                      className={cn(
                        'tw:flex tw:size-4 tw:items-center tw:justify-center tw:rounded-[4px] tw:border',
                        isSelected
                          ? 'tw:bg-primary tw:border-primary tw:text-primary-foreground'
                          : 'tw:border-input tw:[&_svg]:invisible',
                      )}
                    >
                      <Check className="tw:text-primary-foreground tw:size-3.5" />
                    </div>
                    {option.icon && (
                      <option.icon className="tw:text-muted-foreground tw:size-4" />
                    )}
                    <span className="tw:break-all">{option.label}</span>
                    {atLeastOneHasValue && (
                      <span className="tw:text-muted-foreground tw:ml-auto tw:flex tw:size-4 tw:items-center tw:justify-center tw:font-mono tw:text-xs">
                        {facets?.get(option.value) || '-'}
                      </span>
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {selectedValues.size > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={() => column?.setFilterValue(undefined)}
                    className="tw:justify-center tw:text-center"
                  >
                    {i18n.t('Common:clearFilters')}
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
